import { useState } from "react"

import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import { ChevronRight } from "lucide-react"

import { HighlightedJson } from "./HighlightedJson"

export const MATCH_RESULTS = {
  MATCH: "MATCH",
  DIFF: "DIFF",
  NOT_FOUND: "NOT_FOUND",
} as const

export type MatchResult = (typeof MATCH_RESULTS)[keyof typeof MATCH_RESULTS]

export const MATCH_RESULT_LABELS = {
  yes: "Match",
  no: "Diff",
  unknown: "Not Found",
} as const

export interface ApiStateExplanation {
  api_url: string
  response_status: number
  response_body: unknown
  match_path?: string
  extracted_value?: unknown
  expected_body: unknown
  match_result: MatchResult
}

export function toBinaryMo<
  T extends { string_value?: string | null; float_value?: number | null },
>(metricOutput: T): T {
  const mapped =
    metricOutput.string_value === MATCH_RESULTS.MATCH
      ? ANNOTATION_VALUE.YES
      : metricOutput.string_value === MATCH_RESULTS.DIFF
        ? ANNOTATION_VALUE.NO
        : metricOutput.string_value === MATCH_RESULTS.NOT_FOUND
          ? ANNOTATION_VALUE.UNKNOWN
          : metricOutput.string_value
  return { ...metricOutput, string_value: mapped }
}

export function getApiStateExplanation(
  metricOutput: {
    result?: Record<string, unknown> | null
    result_json?: Record<string, unknown> | null
  },
  metricMetadata: Record<string, unknown> | null | undefined,
): ApiStateExplanation | null {
  const result = metricOutput.result as
    | {
        match_result?: string
        actual_value?: { status?: number; body?: unknown }
        expected_value?: unknown
        extracted_value?: unknown
      }
    | undefined

  if (result?.match_result) {
    const apiUrl = metricMetadata?.api_url
    const matchPath = metricMetadata?.match_path
    return {
      api_url: typeof apiUrl === "string" ? apiUrl : "",
      response_status: result.actual_value?.status ?? 0,
      response_body: result.actual_value?.body,
      match_path: typeof matchPath === "string" ? matchPath : undefined,
      extracted_value: result.extracted_value,
      expected_body: result.expected_value,
      match_result: result.match_result as MatchResult,
    }
  }

  return (metricOutput.result_json as ApiStateExplanation | null) ?? null
}

interface ApiStateDeepDiveProps {
  explanation: ApiStateExplanation
  matchResult: MatchResult
}

export function ApiStateDeepDive({
  explanation,
  matchResult,
}: ApiStateDeepDiveProps) {
  const [showFullResponse, setShowFullResponse] = useState(false)

  const formatJson = (value: unknown): string => {
    try {
      return JSON.stringify(value, null, 2)
    } catch {
      return String(value)
    }
  }

  const resultDisplay = (() => {
    switch (matchResult) {
      case MATCH_RESULTS.MATCH:
        return {
          text: "✓ Match",
          className: "text-coval-success-text",
          message: "The API response matched the expected value.",
          bgClassName: "bg-coval-success-surface border-coval-success-border",
          textClassName: "text-coval-success-on-surface",
        }
      case MATCH_RESULTS.DIFF:
        return {
          text: "✗ Diff",
          className: "text-coval-danger-text",
          message:
            "The API response was found but did not match the expected value.",
          bgClassName: "bg-coval-danger-surface border-coval-danger-border",
          textClassName: "text-coval-danger-on-surface",
        }
      case MATCH_RESULTS.NOT_FOUND:
        return {
          text: "⚠ Not Found",
          className: "text-coval-warning-text",
          message: "The match path was not found in the API response body.",
          bgClassName: "bg-coval-warning-surface border-coval-warning-border",
          textClassName: "text-coval-warning-on-surface",
        }
      default:
        return {
          text: "N/A",
          className: "text-coval-text-muted",
          message: "Unknown match result.",
          bgClassName: "bg-surface-1 border-coval-border",
          textClassName: "text-coval-text-muted",
        }
    }
  })()

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold">Result</h3>
        <p className={resultDisplay.className}>{resultDisplay.text}</p>
      </div>

      <div>
        <h3 className="font-semibold">API URL</h3>
        <p className="font-mono text-sm break-all">{explanation.api_url}</p>
      </div>

      <div>
        <h3 className="font-semibold">Response Status</h3>
        <p>{explanation.response_status}</p>
      </div>

      {explanation.match_path && (
        <div>
          <h3 className="font-semibold">Match Path</h3>
          <p className="font-mono text-sm">{explanation.match_path}</p>
        </div>
      )}

      {explanation.match_path && (
        <div>
          <h3 className="font-semibold">Extracted Value</h3>
          <pre className="bg-muted/30 p-3 rounded-lg text-xs overflow-x-auto">
            {formatJson(explanation.extracted_value)}
          </pre>
        </div>
      )}

      <div>
        <button
          type="button"
          onClick={() => setShowFullResponse(!showFullResponse)}
          aria-label={
            showFullResponse ? "Collapse response body" : "Expand response body"
          }
          className="w-full flex items-center justify-between mb-2 hover:bg-surface-1 transition-colors rounded-lg p-2 -ml-2"
        >
          <h3 className="font-semibold">Full Response Body</h3>
          <ChevronRight
            className={`w-4 h-4 text-coval-text-muted transition-transform ${
              showFullResponse ? "rotate-90" : ""
            }`}
          />
        </button>
        {showFullResponse && (
          <pre className="bg-muted/30 p-3 rounded-lg text-xs overflow-x-auto">
            {explanation.match_path &&
            explanation.extracted_value !== undefined ? (
              <HighlightedJson
                json={explanation.response_body}
                matchPath={explanation.match_path}
                extractedValue={explanation.extracted_value}
              />
            ) : (
              formatJson(explanation.response_body)
            )}
          </pre>
        )}
      </div>

      <div>
        <h3 className="font-semibold">Expected Value</h3>
        <pre className="bg-muted/30 p-3 rounded-lg text-xs overflow-x-auto">
          {formatJson(explanation.expected_body)}
        </pre>
      </div>

      <div className={`p-3 ${resultDisplay.bgClassName} border rounded-lg`}>
        <p className={`text-sm ${resultDisplay.textClassName}`}>
          {resultDisplay.message}
        </p>
      </div>
    </div>
  )
}
